/**
 * @file Represents the navigation bar of the application.
 * The navigation bar includes the application's logo and a sign-in/register button.
 * It uses a styled version of the MUI AppBar component for a custom look.
 */
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { Stack, styled, useTheme } from '@mui/system';
import React, { useEffect, useState } from 'react';
import logo from '../../assets/solid-white-essentia-logo.webp';
import { useUser, useUserStatus } from '../../hooks/user';
import { Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AvatarButton } from '../buttons/AvatarButton';
import { SignInButton } from '../buttons/SignInButton';
import { ProfileMenuItem } from '../menus/user/items/ProfileMenuItem';
import { LogoutMenuItem } from '../menus/user/items/LogoutMenuItem';
import { HeaderMenuItem } from '../menus/user/items/HeaderMenuItem';
import { UserMenu } from '../menus/user/UserMenu';
import { GoProButton } from '../buttons/GoProButton';
import GoProDialog from '../forms/GoProDialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import { InsightLogo } from '../logos/InsightLogo';
import ProfileDialog, { PROFILE_DIALOG_STATE } from '../forms/ProfileDialog';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    paddingLeft: '12px',
    paddingRight: '12px',
    height: '80px', // This is not using 'rem' on purpose
    backgroundColor: theme.palette.background.header,
    overflow: 'hidden', // This line disables the scrollbar in the header
    [theme.breakpoints.down('sm')]: {
        paddingLeft: 0,
        paddingRight: 0
    }
}));

const StyledToolbar = styled(Toolbar)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '80px' // This is not using 'rem' on purpose
});

const StyledEssentiaLogo = styled('img')(({ theme }) => ({
    width: '145px', // This is not using 'rem' on purpose
    [theme.breakpoints.down('sm')]: {
        width: '100px'
    },
    '&:hover': {
        cursor: 'pointer'
    }
}));

const insightLogoFontSizes = {
    basic: { desktop: 21, mobile: 16 },
    pro: { desktop: 24, mobile: 17 },
    advantage: { desktop: 23, mobile: 16 }
};

/**
 * The `NavBar` component renders the main navigation bar for the application.
 * This navigation bar showcases the application's logo on the left and a sign-in/register button on the right.
 * It also contains logic to hide the sign-in/register text when the current route is '/app'.
 *
 * @component
 * @returns {React.ReactElement} The rendered NavBar component.
 */
export const NavBar = () => {
    const navigate = useNavigate();

    const { user, isAuthenticated } = useAuth0();
    const userHook = useUser();
    const { isRegistered } = useUserStatus();
    const theme = useTheme();

    const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'));

    const subscriptionType = user?.subscription_type ?? 'unauthorized';

    const [openProfileDialog, setOpenProfileDialog] = useState(
        PROFILE_DIALOG_STATE.Closed
    );
    const [insightProDetailsOpen, setInsightProDetailsOpen] = useState(false);

    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const openUserMenu = Boolean(userMenuAnchor);
    const location = useLocation();

    useEffect(() => {
        const registered = isRegistered();
        /*
         * if the user is not available === undefined |
         * if the user is available but complete === true; |
         * if the user is available but not complete === false; | X
         *
         * */

        if (!isAuthenticated || registered === undefined) {
            return;
        }

        if (location.pathname !== '/app') {
            return;
        }

        if (registered === null || !registered) {
            setOpenProfileDialog(PROFILE_DIALOG_STATE.OpenMandatory);
        }
    }, [userHook.user.internalDetails]);

    const handleClose = () => {
        setUserMenuAnchor(null);
    };
    const handleProfileClick = () => {
        setOpenProfileDialog(PROFILE_DIALOG_STATE.OpenOptional);
        setUserMenuAnchor(null);
    };
    const handleAvatarClick = (event) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleHomePageClick = () => {
        navigate('/', { replace: true });
    };

    return (
        <StyledAppBar id={'navBar'} data-analyticsid='nav-bar'>
            <StyledToolbar>
                <StyledEssentiaLogo
                    src={logo}
                    alt='Logo'
                    onClick={handleHomePageClick}
                    data-analyticsid='essentia-logo'
                />
                {isAuthenticated ? (
                    <>
                        {isBelowSm && subscriptionType === 'basic' && (
                            <Box sx={{ marginLeft: 1, marginRight: 1 }}>
                                <GoProButton
                                    onClick={() =>
                                        setInsightProDetailsOpen(true)
                                    }
                                />
                            </Box>
                        )}
                        <Stack
                            direction='row'
                            alignItems={'center'}
                            spacing={{ xs: 1.5, sm: 3 }}
                        >
                            {!isBelowSm && subscriptionType === 'basic' && (
                                <GoProButton
                                    onClick={() =>
                                        setInsightProDetailsOpen(true)
                                    }
                                    sxProps={{
                                        fontSize: 17,
                                        height: 38,
                                        width: 95,
                                        lineHeight: 1
                                    }}
                                />
                            )}
                            <InsightLogo
                                fontSize={
                                    insightLogoFontSizes[subscriptionType]?.[
                                        isBelowSm ? 'mobile' : 'desktop'
                                    ] || null
                                }
                                color={theme.palette.common.white}
                                subType={subscriptionType}
                            />
                            <AvatarButton onClick={handleAvatarClick} />
                        </Stack>
                    </>
                ) : (
                    <SignInButton />
                )}

                <UserMenu
                    anchor={userMenuAnchor}
                    open={openUserMenu}
                    onClose={handleClose}
                >
                    <HeaderMenuItem />
                    <Divider />
                    <ProfileMenuItem onClick={handleProfileClick} />
                    <LogoutMenuItem />
                </UserMenu>
                <ProfileDialog
                    dialogState={openProfileDialog}
                    onFormClose={() =>
                        setOpenProfileDialog(PROFILE_DIALOG_STATE.Closed)
                    }
                />
                <GoProDialog
                    open={insightProDetailsOpen}
                    onFormClose={() => setInsightProDetailsOpen(false)}
                    onSubmissionViewClose={() =>
                        setInsightProDetailsOpen(false)
                    }
                />
            </StyledToolbar>
        </StyledAppBar>
    );
};
